import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, ArrowRight } from "lucide-react";
import { api } from "../lib/config";
import { toast } from "sonner";
import { SectionOverline } from "./Ornament";

const PROFILES = [
  { value: "graveur-existant", label: "Je suis déjà graveur / artisan" },
  { value: "futur-graveur", label: "Je souhaite me lancer dans la gravure" },
  { value: "curieux", label: "Je découvre, je me renseigne" },
];

const FranchiseForm = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", city: "", profile: PROFILES[0].value, message: "", consent: false,
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Vous devez accepter le traitement de vos données pour envoyer.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/franchise", form);
      toast.success("Candidature envoyée ! Nous revenons vers vous rapidement.");
      setForm({ full_name: "", email: "", phone: "", city: "", profile: PROFILES[0].value, message: "", consent: false });
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi.");
    }
    setLoading(false);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="py-20 bg-[#DDD4B2] text-[#171717]" data-testid="franchise-section">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <SectionOverline>Réseau partenaire</SectionOverline>
        <h3 className="font-serif text-3xl sm:text-4xl mt-3 mb-5">Rejoignez le réseau Passion Verre</h3>
        <p className="text-base leading-relaxed text-[#171717]/85 mb-8">
          Vous êtes graveur ou vous souhaitez vous lancer dans la personnalisation laser ? Rejoignez le réseau Passion Verre et apparaissez sur notre carte ! Bénéficiez de notre expérience, notre accompagnement et notre savoir-faire.
        </p>
        {!open && (
          <button onClick={() => setOpen(true)} className="inline-flex items-center gap-3 bg-[#171717] text-[#DDD4B2] px-10 py-4 text-sm uppercase tracking-[0.18em] hover:bg-[#9A8640] hover:text-[#171717] transition-all" data-testid="franchise-cta">
            Candidater <ArrowRight size={16} />
          </button>
        )}
        {open && (
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={submit}
            className="text-left mt-10 space-y-5 bg-[#171717] text-[#DDD4B2] p-8 sm:p-10 border border-[#9A8640]/40"
            data-testid="franchise-form"
          >
            <h4 className="font-serif text-2xl text-[#DDD4B2] text-center mb-6">Votre candidature</h4>
            <div className="grid sm:grid-cols-2 gap-5">
              <input required placeholder="Nom complet" value={form.full_name} onChange={set("full_name")} className="input-elegant" data-testid="franchise-name" />
              <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className="input-elegant" data-testid="franchise-email" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <input placeholder="Téléphone" value={form.phone} onChange={set("phone")} className="input-elegant" data-testid="franchise-phone" />
              <input required placeholder="Ville" value={form.city} onChange={set("city")} className="input-elegant" data-testid="franchise-city" />
            </div>
            <select required value={form.profile} onChange={set("profile")} className="input-elegant bg-[#171717]" data-testid="franchise-profile">
              {PROFILES.map((p) => <option key={p.value} value={p.value} className="bg-[#171717]">{p.label}</option>)}
            </select>
            <textarea required rows={4} placeholder="Parlez-nous de votre projet, votre expérience, vos attentes…" value={form.message} onChange={set("message")} className="input-elegant resize-none" data-testid="franchise-message" />
            <label className="flex items-start gap-3 text-xs text-[#DDD4B2]/85 cursor-pointer" data-testid="franchise-consent-label">
              <input
                type="checkbox"
                required
                checked={form.consent}
                onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                className="mt-1 accent-[#9A8640] w-4 h-4 shrink-0"
                data-testid="franchise-consent"
              />
              <span className="leading-relaxed">
                J'accepte que Passion Verre traite ces informations pour étudier ma candidature. Voir notre{" "}
                <a href="/politique-confidentialite" target="_blank" rel="noopener noreferrer" className="text-[#9A8640] underline hover:text-[#DDD4B2]">
                  Politique de confidentialité
                </a>.
              </span>
            </label>
            <div className="flex gap-3 justify-end pt-2">
              <button type="button" onClick={() => setOpen(false)} className="btn-outline px-6 py-3 text-xs uppercase">Annuler</button>
              <button type="submit" disabled={loading || !form.consent} className="btn-gold px-6 py-3 text-xs uppercase inline-flex items-center gap-2 disabled:opacity-60" data-testid="franchise-submit">
                {loading ? "Envoi…" : <>Envoyer <Send size={14} /></>}
              </button>
            </div>
          </motion.form>
        )}
      </div>
    </div>
  );
};

export default FranchiseForm;
